import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, LifeBuoy, BookOpen, ChevronRight, ArrowLeft, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';

export default function CommunityLanding() {
    const navigate = useNavigate();
    const { user } = useAuth() || { user: null };

    // Unread counts
    const [unreadChat, setUnreadChat] = useState(0);
    const [unreadSupport, setUnreadSupport] = useState(0);

    // Get unread count for Community Chat
    useEffect(() => {
        if (!user?.email) return;

        // Get user's last read timestamp for community chat
        const userReadRef = doc(db, 'user_read_status', `${user.email}_community_chat`);

        const fetchUnread = async () => {
            try {
                const readDoc = await getDoc(userReadRef);
                const lastRead = readDoc.exists() ? readDoc.data().last_read?.toDate() : new Date(0);

                // Subscribe to new messages count
                const messagesRef = collection(db, 'community_chat');
                const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
                    let count = 0;
                    snapshot.docs.forEach(doc => {
                        const msgDate = doc.data().created_at?.toDate();
                        if (msgDate && msgDate > lastRead && doc.data().author_email !== user.email) {
                            count++;
                        }
                    });
                    setUnreadChat(count);
                });

                return unsubscribe;
            } catch (error) {
                console.error('Error fetching unread chat:', error);
            }
        };

        fetchUnread();
    }, [user?.email]);

    // Get unread count for Admin Support
    useEffect(() => {
        if (!user?.email) return;

        const userReadRef = doc(db, 'user_read_status', `${user.email}_admin_support`);

        const fetchUnread = async () => {
            try {
                const readDoc = await getDoc(userReadRef);
                const lastRead = readDoc.exists() ? readDoc.data().last_read?.toDate() : new Date(0);

                // Subscribe to support messages for this user
                const messagesRef = collection(db, 'admin_support');
                const q = query(messagesRef, where('user_email', '==', user.email));

                const unsubscribe = onSnapshot(q, (snapshot) => {
                    let count = 0;
                    snapshot.docs.forEach(doc => {
                        const msgDate = doc.data().created_at?.toDate();
                        // Count admin responses that are unread
                        if (msgDate && msgDate > lastRead && doc.data().author_role === 'admin') {
                            count++;
                        }
                    });
                    setUnreadSupport(count);
                });

                return unsubscribe;
            } catch (error) {
                console.error('Error fetching unread support:', error);
            }
        };

        fetchUnread();
    }, [user?.email]);

    const sections = [
        {
            id: 'chat',
            label: 'Community Chat',
            path: '/chat',
            icon: MessageSquare,
            color: 'bg-blue-500',
            desc: 'Join community discussion with fellow traders',
            unread: unreadChat
        },
        {
            id: 'support',
            label: 'Admin Support',
            path: '/admin-support',
            icon: LifeBuoy,
            color: 'bg-emerald-500',
            desc: 'Get direct help from admin team',
            unread: unreadSupport
        },
        {
            id: 'kb',
            label: 'Knowledge Base',
            path: '/knowledge-base',
            icon: BookOpen,
            color: 'bg-purple-500',
            desc: 'Trading guides & resources',
            unread: 0
        },
        {
            id: 'settings',
            label: 'Settings',
            path: '/student/settings',
            icon: Settings,
            color: 'bg-gray-600',
            desc: 'Manage profile & preferences',
            unread: 0
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black">
            <div className="bg-[#ffffff] dark:!bg-black border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">


                    {/* Breadcrumbs */}
                    <div className="flex items-center text-xs md:text-sm text-gray-500 mb-6 font-medium">
                        <Link to="/student/dashboard" className="hover:text-blue-600 transition-colors text-gray-500 dark:text-gray-400">Dashboard</Link>
                        <ChevronRight size={14} className="mx-2 text-gray-400" />
                        <span className="text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">Community</span>
                    </div>

                    {/* Title Section */}
                    <div className="flex items-center mb-6 md:mb-8">
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-3 md:p-4 rounded-xl mr-4 shadow-lg shadow-blue-100 flex-shrink-0">
                            <MessageSquare size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">Community Hub</h1>
                            <p className="text-gray-500 dark:text-gray-400 text-sm md:text-lg">
                                Connect, learn, and grow with fellow traders.
                            </p>
                        </div>
                    </div>

                    {/* Navigation Cards - Vertical List */}
                    <div className="flex flex-col gap-4 max-w-3xl">
                        {sections.map((section) => (
                            <Link
                                key={section.id}
                                to={section.path}
                                className="relative flex items-center justify-between p-4 md:p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-[#ffffff] dark:!bg-black hover:border-blue-300 dark:hover:border-blue-700 active:bg-gray-50 dark:active:bg-gray-900 transition-all duration-200 group"
                            >
                                <div className="flex items-center">
                                    <div className={`relative p-2.5 md:p-3 rounded-lg mr-3 md:mr-4 ${section.color} text-white shadow-sm`}>
                                        <section.icon size={20} />

                                        {/* Unread Badge on Icon */}
                                        {section.unread > 0 && (
                                            <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md">
                                                {section.unread > 99 ? '99+' : section.unread}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm md:text-base text-gray-800 dark:text-gray-100">
                                            {section.label}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{section.desc}</p>
                                    </div>
                                </div>

                                <ChevronRight size={20} className="text-gray-400 group-hover:text-blue-500 transition-transform group-hover:translate-x-1" />
                            </Link>
                        ))}
                    </div>

                    {/* Hint Text for Mobile */}
                    <p className="text-center text-xs text-gray-400 mt-4 md:hidden">
                        Tap to open section
                    </p>
                </div>
            </div>
        </div>
    );
}
